﻿// Map Drawing Module - provides drawing tools with measurement labels
// iOS Fix: "Distance measure" becomes exactly ONE straight segment.
// - On iOS in drawLine mode, after 2 vertices exist, we commit the line ourselves.
// - This avoids unreliable dbl-tap/dbl-click and avoids non-public finish APIs.
//
// Mobile UX improvement (NO ghost line):
// - On iOS (mobile) in drawLine/drawPolygon mode, show a temporary start pin on tap.
//   (DrawingManager events often don't fire on the first vertex, so we use a map click handler.)
// - Clear it on commit/cancel/complete.
//
// Mobile UX improvement (editable vertices for distance):
// - On iOS drawLine, after 2 taps we commit the line, then immediately enter edit mode
//   so the user can drag vertices to refine placement.
// - User must press the existing "Done" action (calls finishDrawing()) to finalize measurement + exit.

let map = null;
let drawingManager = null;
let drawingToolbar = null;

let labelDataSource = null;
let labelFeatures = new Map();

// Mobile preview (start point only)
let mobilePreviewSource = null;
let mobilePreviewLayer = null;
let mobileStartFeature = null;

// iOS line auto-finish toggle (only for drawLine)
let iosLineAutoFinishEnabled = true;

// Guard so we only auto-finish once per drawing start
let _autoFinishArmed = false;
let _autoFinishLastTs = 0;

// Mobile tap handler (to drop start marker reliably)
let _mobileStartTapWired = false;
let _mobileTapLastTs = 0;

// Track "committed then editing" line on iOS so Done can finalize
let _pendingEditShape = null;

let _suppressInspectUntil = 0;

function suppressInspectFor(ms) {
    _suppressInspectUntil = Math.max(_suppressInspectUntil, Date.now() + ms);
}

function shouldSuppressInspect() {
    return Date.now() < _suppressInspectUntil;
}

function isDrawingActive() {
    const mode = drawingManager?.getOptions?.()?.mode;
    return mode === atlas.drawing.DrawingMode.drawLine ||
        mode === atlas.drawing.DrawingMode.drawPolygon ||
        mode === getEditGeometryMode();
}

function isIOS() {
    const ua = navigator.userAgent || '';
    const iOS = /iPad|iPhone|iPod/.test(ua);
    const iPadOS13Plus = (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    return iOS || iPadOS13Plus;
}

function getInteractionType() {
    // Prefer enum when available; fallback to string
    const IT = atlas?.drawing?.InteractionType;
    if (IT) return isIOS() ? IT.click : IT.hybrid;
    return isIOS() ? 'click' : 'hybrid';
}

function getEditGeometryMode() {
    // Some builds expose enum; some accept a string.
    return atlas?.drawing?.DrawingMode?.editGeometry ?? 'edit-geometry';
}

function getOptionsSafe() {
    try { return drawingManager?.getOptions?.(); } catch { return null; }
}

// Helper function to extract geometry from shape/event
function getGeometry(shape) {
    if (!shape) return null;

    if (shape.getGeometry && typeof shape.getGeometry === 'function') return shape.getGeometry();
    if (shape.geometry) return shape.geometry;
    if (shape.data && shape.data.geometry) return shape.data.geometry;

    if (shape.toJson && typeof shape.toJson === 'function') {
        const json = shape.toJson();
        if (json && json.geometry) return json.geometry;
    }

    return null;
}

function getCenterPoint(geom) {
    const { type, coordinates } = geom;

    if (type === 'LineString') {
        const midIndex = Math.floor(coordinates.length / 2);
        return coordinates[midIndex];
    } else if (type === 'Polygon') {
        let sumLat = 0, sumLng = 0, count = 0;
        const ring = coordinates[0];
        for (let i = 0; i < ring.length - 1; i++) {
            sumLng += ring[i][0];
            sumLat += ring[i][1];
            count++;
        }
        return [sumLng / count, sumLat / count];
    }
    return null;
}

function updateMeasurement(shape, final = false) {
    const geom = getGeometry(shape);

    if (!geom) {
        if (final) console.log('No geometry for shape:', shape);
        return;
    }

    const { type, coordinates } = geom;
    if (!coordinates || !Array.isArray(coordinates) || coordinates.length === 0) return;

    let measurementValue = '';

    if (type === 'LineString') {
        if (coordinates.length < 2) return;
        const lengthFeet = atlas.math.getLengthOfPath(coordinates, 'feet');
        measurementValue = `${lengthFeet.toFixed(2)} ft`;
    } else if (type === 'Polygon') {
        if (!coordinates[0] || coordinates[0].length < 3) return;
        const areaSqMeters = atlas.math.getArea(geom);
        const areaSqFeet = areaSqMeters * 10.7639;
        measurementValue = `${areaSqFeet.toFixed(2)} sq ft`;
    }

    if (!measurementValue) return;

    console.log(final ? '[final]' : '[live]', measurementValue);

    // Only show labels when drawing is complete
    if (!final) return;

    const centerPoint = getCenterPoint(geom);
    if (!centerPoint) return;

    // Get or create a unique ID for this shape
    let shapeId = shape.getId ? shape.getId() : null;
    if (!shapeId) {
        shapeId = `shape_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    // Remove old label if it exists
    if (labelFeatures.has(shapeId)) {
        labelDataSource.remove(labelFeatures.get(shapeId));
    }

    // Create new label feature
    const labelFeature = new atlas.data.Feature(
        new atlas.data.Point(centerPoint),
        { label: measurementValue, shapeId }
    );

    labelFeatures.set(shapeId, labelFeature);
    labelDataSource.add(labelFeature);
}

function setMode(mode) {
    drawingManager.setOptions({
        mode,
        interactionType: getInteractionType()
    });
}

function cleanupUIAfterComplete() {
    // Cancel drawing mode after completion - user must click button again to draw
    try { drawingManager?.setOptions?.({ mode: atlas.drawing.DrawingMode.idle }); } catch { }

    // Remove active class from both buttons
    const lineBtn = document.getElementById('btn-measure-distance');
    const polygonBtn = document.getElementById('btn-measure-area');
    if (lineBtn) lineBtn.classList.remove('active');
    if (polygonBtn) polygonBtn.classList.remove('active');

    // Re-enable map click handler - MapInspect will be imported
    if (typeof window.MapInspect !== 'undefined' && window.MapInspect.enableClickHandler) {
        window.MapInspect.enableClickHandler();
    }
}

// ---- Mobile Start Indicator helpers (iOS only) ----
function clearMobilePreview() {
    try { mobilePreviewSource?.clear?.(); } catch { /* ignore */ }
    mobileStartFeature = null;
}

function showMobileStartPoint(coord) {
    if (!isIOS()) return;
    if (!mobilePreviewSource || !coord) return;

    clearMobilePreview();

    mobileStartFeature = new atlas.data.Feature(
        new atlas.data.Point(coord),
        {}
    );

    mobilePreviewSource.add(mobileStartFeature);
}

// Wire a map click handler to drop the marker (DrawingManager doesn't reliably fire on first vertex)
function wireMobileStartTapOnce() {
    if (_mobileStartTapWired) return;
    _mobileStartTapWired = true;

    try {
        map.events.add('click', (e) => {
            if (!isIOS()) return;

            const opts = getOptionsSafe();
            const mode = opts?.mode;

            const isLine = mode === atlas.drawing.DrawingMode.drawLine;
            const isPoly = mode === atlas.drawing.DrawingMode.drawPolygon;
            if (!isLine && !isPoly) return;

            // Only when our line tool is active; polygon can run whenever drawPolygon is active
            if (isLine && !_autoFinishArmed) return;

            // Debounce: iOS/Safari can fire duplicates
            const now = Date.now();
            if (now - _mobileTapLastTs < 120) return;
            _mobileTapLastTs = now;

            // Use geographic coordinate directly
            const coord = e?.position;
            if (!coord || !Array.isArray(coord) || coord.length < 2) return;

            // Update marker on every tap while drawing
            showMobileStartPoint(coord);
        });
    } catch (err) {
        console.warn('[MapDrawing] wireMobileStartTapOnce failed:', err);
    }
}

function enterEditModeForShape(shape) {
    if (!shape) return;

    try {
        suppressInspectFor(1500);

        // Keep MapInspect disabled while editing
        if (typeof window.MapInspect !== 'undefined' && window.MapInspect.disableClickHandler) {
            window.MapInspect.disableClickHandler();
        }

        // Switch DrawingManager into edit geometry mode, then edit the shape.
        // (Some builds accept editGeometry enum; others accept 'edit-geometry'.)
        drawingManager.setOptions({
            mode: getEditGeometryMode(),
            interactionType: getInteractionType(),
            dragHandleStyle: {
                htmlContent: '<div style="width:30px; height:30px; background-color:rgba(168, 85, 247, 0.9); border-radius:50%;"></div>',
                anchor: 'center' // Ensures the larger handle is centered on the vertex
            }
        });

        if (typeof drawingManager.edit === 'function') {
            drawingManager.edit(shape);
        }

        _pendingEditShape = shape;

        // Update line drawing alert message for edit mode
        if (window.vueAppInstance) {
            window.vueAppInstance.lineDrawingAlertMessage = 'Drag the purple points to adjust. Tap Finish to calculate distance.';
        }
    } catch (err) {
        console.warn('[MapDrawing] enterEditModeForShape failed:', err);
    }
}

// --- iOS: Commit a 2-point line ourselves, THEN enter edit mode (so vertices are draggable) ---
function commitLine(coords) {
    if (!coords || coords.length < 2) return;

    suppressInspectFor(900);

    // Clear the start marker once we commit
    clearMobilePreview();

    // Stop the in-progress sketch
    try { drawingManager.setOptions({ mode: atlas.drawing.DrawingMode.idle }); } catch { }

    try {
        const source = drawingManager.getSource?.();
        if (!source || !source.add) {
            console.warn('[MapDrawing] drawingManager.getSource() not available; cannot commit line.');
            return;
        }

        const line = new atlas.data.LineString(coords);
        const shape = new atlas.Shape(line);
        source.add(shape);

        // NEW: enter edit mode instead of finalizing immediately
        enterEditModeForShape(shape);

        // Do NOT finalize measurement yet. User will tap Done (finishDrawing()) after adjusting.
        // Keep the Distance button highlighted until Done/cancel.
    } catch (err) {
        console.error('[MapDrawing] commitLine failed:', err);
    }
}

function handleAutoFinishLine(e) {
    if (!isIOS()) return;
    if (!iosLineAutoFinishEnabled) return;

    const opts = getOptionsSafe();
    if (opts?.mode !== atlas.drawing.DrawingMode.drawLine) return;

    // Only when we've armed it for this drawing session
    if (!_autoFinishArmed) return;

    const shape = e?.shape || e;
    const geom = getGeometry(shape);
    if (!geom || geom.type !== 'LineString') return;

    const coords = geom.coordinates || [];
    if (coords.length < 2) return;

    // Throttle + one-shot
    const now = Date.now();
    if (now - _autoFinishLastTs < 200) return;
    _autoFinishLastTs = now;

    // Disarm so we only commit once per draw start
    _autoFinishArmed = false;

    // Commit EXACTLY the first 2 vertices for a straight segment
    commitLine([coords[0], coords[1]]);
}

function wireAutoFinishEventsOnce() {
    try { map.events.add('drawingchanging', drawingManager, handleAutoFinishLine); } catch { }
    try { map.events.add('drawingchanged', drawingManager, handleAutoFinishLine); } catch { }
}

function finalizePendingEditIfAny() {
    if (!_pendingEditShape) return false;

    try {
        suppressInspectFor(900);

        // Exit edit mode
        try { drawingManager?.setOptions?.({ mode: atlas.drawing.DrawingMode.idle }); } catch { }

        // Compute final measurement + label
        updateMeasurement(_pendingEditShape, true);

        _pendingEditShape = null;

        cleanupUIAfterComplete();

        // Hide drawing alerts in Vue app
        if (window.vueAppInstance) {
            window.vueAppInstance.showLineDrawingAlert = false;
            window.vueAppInstance.showPolygonDrawingAlert = false;
        }

        return true;
    } catch (err) {
        console.error('[MapDrawing] finalizePendingEditIfAny failed:', err);
        return false;
    }
}

function cancelPendingEditIfAny(removeShape = true) {
    if (!_pendingEditShape) return false;

    try {
        const shape = _pendingEditShape;
        _pendingEditShape = null;

        try { drawingManager?.setOptions?.({ mode: atlas.drawing.DrawingMode.idle }); } catch { }

        if (removeShape) {
            const source = drawingManager?.getSource?.();
            try { source?.remove?.(shape); } catch { /* ignore */ }
        }

        return true;
    } catch {
        return false;
    }
}

// Public API
const MapDrawing = {
    initialize: function (mapInstance) {
        map = mapInstance;

        drawingManager = new atlas.drawing.DrawingManager(map, {
            toolbar: drawingToolbar,
            interactionType: getInteractionType()
        });

        // Data source and layer for measurement labels
        labelDataSource = new atlas.source.DataSource();
        map.sources.add(labelDataSource);

        const labelLayer = new atlas.layer.SymbolLayer(labelDataSource, null, {
            textOptions: {
                textField: ['get', 'label'],
                size: 14,
                font: ['StandardFont-Bold'],
                color: '#ffffff',
                haloColor: '#000000',
                haloWidth: 2,
                offset: [0, 0]
            },
            iconOptions: { image: 'none' }
        });
        map.layers.add(labelLayer);

        // Mobile preview datasource/layer (start pin)
        mobilePreviewSource = new atlas.source.DataSource();
        map.sources.add(mobilePreviewSource);

        mobilePreviewLayer = new atlas.layer.SymbolLayer(mobilePreviewSource, null, {
            iconOptions: {
                image: 'pin-round-darkblue',
                anchor: 'bottom',
                size: 0.9,
                allowOverlap: true
            }
        });
        map.layers.add(mobilePreviewLayer);

        labelFeatures = new Map();

        map.events.add('ready', () => {
            wireAutoFinishEventsOnce();
            wireMobileStartTapOnce();
        });

        // Normal completion path (desktop / non-iOS, polygons, etc.)
        map.events.add('drawingcomplete', drawingManager, (e) => {
            suppressInspectFor(900);

            clearMobilePreview();

            const shape = e.shape || e;
            if (shape) {
                updateMeasurement(shape, true);
            }
            cleanupUIAfterComplete();

            // On non-mobile, hide the alert when drawing completes (mobile can edit, so keep alert visible)
            if (window.vueAppInstance) {
                const geom = getGeometry(shape);
                const isLine = geom && geom.type === 'LineString';
                const isPolygon = geom && geom.type === 'Polygon';
                // Check if it's non-mobile (not iOS and window width > 576)
                const isNonMobile = !isIOS() && window.innerWidth > 576;
                if (isNonMobile) {
                    if (isLine) {
                        window.vueAppInstance.isLineDrawing = false;
                        window.vueAppInstance.showLineDrawingAlert = false;
                    }
                    if (isPolygon) {
                        window.vueAppInstance.isPolygonDrawing = false;
                        window.vueAppInstance.showPolygonDrawingAlert = false;
                    }
                }
            }
        });
    },

    shouldSuppressInspect: function () {
        return shouldSuppressInspect();
    },

    getDrawingManager: function () {
        return drawingManager;
    },

    clearAll: function () {
        if (drawingManager && drawingManager.getSource) {
            const source = drawingManager.getSource();
            if (source && source.clear) {
                source.clear();
            }
        }

        if (labelDataSource) {
            labelDataSource.clear();
        }

        clearMobilePreview();
        cancelPendingEditIfAny(false);

        labelFeatures.clear();
    },

    isDrawingActive: function () {
        return isDrawingActive();
    },

    startDrawingLine: function () {
        if (!drawingManager) {
            console.error('Drawing manager not initialized');
            return;
        }

        try {
            // If we were editing something, cancel it (and keep it) before starting a new draw
            cancelPendingEditIfAny(false);

            if (typeof window.MapInspect !== 'undefined' && window.MapInspect.disableClickHandler) {
                window.MapInspect.disableClickHandler();
            }

            clearMobilePreview();

            iosLineAutoFinishEnabled = true;
            _autoFinishArmed = true;
            _autoFinishLastTs = 0;

            const btn = document.getElementById('btn-measure-distance');
            if (btn) btn.classList.add('active');

            const polygonBtn = document.getElementById('btn-measure-area');
            if (polygonBtn) polygonBtn.classList.remove('active');

            suppressInspectFor(1500);
            setMode(atlas.drawing.DrawingMode.drawLine);
        } catch (e) {
            console.error('Error starting line drawing:', e);
        }
    },

    startDrawingPolygon: function () {
        if (!drawingManager) {
            console.error('Drawing manager not initialized');
            return;
        }

        try {
            cancelPendingEditIfAny(false);

            if (typeof window.MapInspect !== 'undefined' && window.MapInspect.disableClickHandler) {
                window.MapInspect.disableClickHandler();
            }

            clearMobilePreview();

            iosLineAutoFinishEnabled = false;
            _autoFinishArmed = false;

            const btn = document.getElementById('btn-measure-area');
            if (btn) btn.classList.add('active');

            const lineBtn = document.getElementById('btn-measure-distance');
            if (lineBtn) lineBtn.classList.remove('active');

            suppressInspectFor(1500);
            setMode(atlas.drawing.DrawingMode.drawPolygon);
        } catch (e) {
            console.error('Error starting polygon drawing:', e);
        }
    },

    // "Done" button should call this for BOTH:
    // - polygon completion
    // - iOS line edit completion
    finishDrawing: function () {
        if (!drawingManager) {
            console.error('Drawing manager not initialized');
            return;
        }

        try {
            // NEW: if we're currently editing a line (iOS), finalize it
            if (finalizePendingEditIfAny()) {
                return;
            }

            const options = drawingManager.getOptions();
            if (!options || options.mode === atlas.drawing.DrawingMode.idle) {
                return;
            }

            // Only handle polygon finishing here
            if (options.mode !== atlas.drawing.DrawingMode.drawPolygon) {
                return;
            }

            const source = drawingManager.getSource();
            if (!source) {
                console.warn('Drawing source not available');
                return;
            }

            const shapes = source.getShapes();
            if (!shapes || shapes.length === 0) {
                return;
            }

            const currentShape = shapes[shapes.length - 1];
            if (!currentShape) return;

            const geom = getGeometry(currentShape);
            if (!geom) {
                console.warn('Could not get geometry from shape:', currentShape);
                return;
            }

            let coords = null;

            if (geom.type === 'Polygon') {
                coords = geom.coordinates;
                if (!coords || !coords[0] || coords[0].length < 3) {
                    console.warn('Polygon has insufficient coordinates');
                    return;
                }
            } else if (geom.type === 'LineString') {
                const lineCoords = geom.coordinates;
                if (!lineCoords || lineCoords.length < 3) {
                    console.warn('LineString has insufficient coordinates for polygon');
                    return;
                }
                coords = [lineCoords];
            } else {
                console.warn('Unexpected geometry type:', geom.type);
                return;
            }

            const ring = coords[0];
            const first = ring[0];
            const last = ring[ring.length - 1];

            if (first[0] !== last[0] || first[1] !== last[1]) {
                ring.push([first[0], first[1]]);
            }

            const closedPolygon = new atlas.data.Polygon(coords);
            const finalizedShape = new atlas.Shape(closedPolygon);

            clearMobilePreview();

            source.remove(currentShape);
            source.add(finalizedShape);

            drawingManager.setOptions({ mode: atlas.drawing.DrawingMode.idle });

            updateMeasurement(finalizedShape, true);
            cleanupUIAfterComplete();
        } catch (e) {
            console.error('Error finishing drawing:', e);
        }
    },

    cancelDrawing: function () {
        try {
            // If we were editing a committed line, remove it on cancel (treat as "abandon")
            cancelPendingEditIfAny(true);

            drawingManager?.setOptions?.({ mode: atlas.drawing.DrawingMode.idle });
        } catch { /* ignore */ }

        _autoFinishArmed = false;

        clearMobilePreview();

        if (typeof window.MapInspect !== 'undefined' && window.MapInspect.enableClickHandler) {
            window.MapInspect.enableClickHandler();
        }

        const lineBtn = document.getElementById('btn-measure-distance');
        const polygonBtn = document.getElementById('btn-measure-area');
        if (lineBtn) lineBtn.classList.remove('active');
        if (polygonBtn) polygonBtn.classList.remove('active');

        // Hide drawing alerts in Vue app
        if (window.vueAppInstance) {
            window.vueAppInstance.showLineDrawingAlert = false;
            window.vueAppInstance.showPolygonDrawingAlert = false;
        }
    }
};

// Export for ES6 modules
export default MapDrawing;

// Make it globally accessible for backward compatibility
window.MapDrawing = MapDrawing;
